import { useState, useEffect, useRef, useCallback } from 'react';
import QRCodeLib from 'qrcode';
// QRCodeErrorCorrectionLevel type used implicitly via options
import { generateQRContent, type QRType, type QRFormData, type QROptions } from '../lib/qrUtils';

interface QRResult {
  dataUrl: string;
  svg: string;
  content: string;
  error: string | null;
}

export function useQRCode(type: QRType, data: QRFormData, options: QROptions) {
  const [result, setResult] = useState<QRResult>({ dataUrl: '', svg: '', content: '', error: null });
  const [isGenerating, setIsGenerating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const regenerate = useCallback(async () => {
    const content = generateQRContent(type, data);
    if (!content.trim()) {
      setResult({ dataUrl: '', svg: '', content: '', error: null });
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    try {
      const [dataUrl, svg] = await Promise.all([
        QRCodeLib.toDataURL(content, {
          width: 1024,
          margin: options.margin,
          color: { dark: options.fgColor, light: options.bgColor },
          errorCorrectionLevel: options.level,
        }),
        QRCodeLib.toString(content, {
          type: 'svg',
          width: 1024,
          margin: options.margin,
          color: { dark: options.fgColor, light: options.bgColor },
          errorCorrectionLevel: options.level,
        }),
      ]);
      setResult({ dataUrl, svg, content, error: null });
    } catch (err) {
      setResult({ dataUrl: '', svg: '', content, error: (err as Error).message || 'Generation failed' });
    } finally {
      setIsGenerating(false);
    }
  }, [type, data, options]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      regenerate();
    }, 200);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [regenerate]);

  return { ...result, isGenerating, regenerate };
}
